import { motion, AnimatePresence } from 'motion/react';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import { ImageWrapper } from './ImageWrapper.jsx';
import { SVGWrapper } from './SVGWrapper.jsx';
import { useTheme } from "../hooks/useTheme.jsx";
import { getProductInfo } from '../utils/productHelpers.js';
import { Header } from './Header';
import Currency from '../utils/Currency.js';
import imgDefaultLogo from '../images/defaultLogo.svg';
import defaultVideo from '../videos/promotional-video.mp4';
import { fs } from '../utils/a11y.js';

export function Home({ currency, locale, showHeaderThemeChanger, showHeaderSearchView, onNavigateToProducts, cartItemsCount, onCartClick, openProductById, openCategoryById, onLogoClick, onSearchClick, playlistItems, style, filteredStoreIds, logActivity }) {
  const { theme } = useTheme();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const timer = useRef(null);
  const wasDragging = useRef(false);
  const defaultSlideAfter = 5000;
  const videoRef = useRef(null);

  const banners = useMemo(() => {
    const result = [];
    if (playlistItems && playlistItems.length > 0) {
      playlistItems.map((playlistItem) => {
        if (playlistItem.type === 'image') {
          const image = playlistItem.item;
          result.push({
            id: image.id,
            playlistId: playlistItem.playlistId,
            playlistName: playlistItem.playlistName,
            itemId: playlistItem.id,
            type: 'image',
            title: image.title,
            price: null,
            image: image.url,
            video: null,
            duration: image.duration,
            loop: false,
            target: playlistItem.item?.target || null,
            targetType: playlistItem.item?.target_type || null
          });
        } else if (playlistItem.type === 'video') {
          const video = playlistItem.item;
          result.push({
            id: video.id,
            playlistId: playlistItem.playlistId,
            playlistName: playlistItem.playlistName,
            itemId: playlistItem.id,
            type: 'video',
            title: video.title,
            price: null,
            image: null,
            video: video.url,
            duration: video.duration,
            loop: false,
            target: playlistItem.item?.target || null,
            targetType: playlistItem.item?.target_type || null
          });
        } else if (playlistItem.type === 'product') {
          const product = playlistItem.item;
          if (product) {
            const { commonPrice, mainImage } = getProductInfo(product, filteredStoreIds);
            result.push({
              id: product.id,
              playlistId: playlistItem.playlistId,
              playlistName: playlistItem.playlistName,
              itemId: playlistItem.id,
              type: 'product',
              title: product.label || product.name,
              price: commonPrice,
              image: mainImage,
              video: null,
              duration: null,
              loop: false,
              target: null,
              targetType: null
            });
          }
        }
      });
    } else {
      result.push({
        id: 'default-video',
        type: 'video',
        title: 'Default Video',
        price: null,
        image: null,
        video: defaultVideo,
        duration: null,
        loop: true
      });
    }
    return result;
  }, [playlistItems]);

  const previousSlide = (log) => {
    setDirection(-1);
    const oldBanner = banners[currentSlide];
    const newIndex = currentSlide - 1 < 0 ? banners.length - 1 : currentSlide - 1;
    const newBanner = banners[newIndex];
    const oldPlaylistId = banners[currentSlide].playlistId;
    const oldPlaylistName = banners[currentSlide].playlistName || null;
    const newPlaylistId = banners[newIndex].playlistId;
    const newPlaylistName = banners[newIndex].playlistName || null;
    if (log) logActivity('home', 'playlist-item', 'swipe-right', true, {old_playlist_id: oldPlaylistId, old_playlist_name: oldPlaylistName, old_slide_index: currentSlide, old_slide_id: oldBanner.id, old_slide_title: oldBanner.title || null, old_slide_type: oldBanner.type || null, new_playlist_id: newPlaylistId, new_playlist_name: newPlaylistName, new_slide_index: newIndex, new_slide_id: newBanner.id, new_slide_title: newBanner.title || null, new_slide_type: newBanner.type || null});
    setCurrentSlide(index => index - 1 < 0 ? banners.length - 1 : index - 1);
  };

  const nextSlide = (log) => {
    setDirection(1);
    const oldBanner = banners[currentSlide];
    const newIndex = currentSlide + 1 > banners.length - 1 ? 0 : currentSlide + 1;
    const newBanner = banners[newIndex];
    const oldPlaylistId = banners[currentSlide].playlistId;
    const oldPlaylistName = banners[currentSlide].playlistName || null;
    const newPlaylistId = banners[newIndex].playlistId;
    const newPlaylistName = banners[newIndex].playlistName || null;
    if (log) logActivity('home', 'playlist-item', 'swipe-left', true, {old_playlist_id: oldPlaylistId, old_playlist_name: oldPlaylistName, old_slide_index: currentSlide, old_slide_id: oldBanner.id, old_slide_title: oldBanner.title || null, old_slide_type: oldBanner.type || null, new_playlist_id: newPlaylistId, new_playlist_name: newPlaylistName, new_slide_index: newIndex, new_slide_id: newBanner.id, new_slide_title: newBanner.title || null, new_slide_type: newBanner.type || null});
    setCurrentSlide(index => index + 1 > banners.length - 1 ? 0 : index + 1);
  };

  const stopSlideInterval = useCallback(() => {
    clearInterval(timer.current);
    timer.current = null;
  }, []);

  const startSlideInterval = useCallback((slideAfter = defaultSlideAfter) => {
    stopSlideInterval();
    if (banners.length === 0) return;
    timer.current = setInterval(() => {
      nextSlide(false);
    }, slideAfter);
  }, [banners, stopSlideInterval, nextSlide]);

  useEffect(() => {
    if (!banners || banners.length <= 0) return;
    if (currentSlide >= banners.length) setCurrentSlide(0);
    const currentBanner = banners[currentSlide];
    const type = currentBanner && currentBanner.type ? currentBanner.type : null;
    if (type === 'product') startSlideInterval(defaultSlideAfter);
    else if (type === 'video') stopSlideInterval();
    else if (type === 'image') startSlideInterval(currentBanner.duration ? currentBanner.duration : defaultSlideAfter);
    else stopSlideInterval();
    return () => stopSlideInterval();
  }, [banners, startSlideInterval, stopSlideInterval, currentSlide]);

  const handleItemClick = () => {
    if (banners.length === 0) return;
    if (currentSlide < 0 || currentSlide >= banners.length) return;
    const currentBanner = banners[currentSlide];
    const id = currentBanner.id;
    const type = currentBanner.type;
    onNavigateToProducts();
    if (openProductById && type === 'product' && id) {
      const productId = currentBanner.id;
      const productName = currentBanner.title || null;
      const playlistId = currentBanner.playlistId;
      const playlistName = currentBanner.playlistName || null;
      logActivity('home', 'product-image', 'click', true, {playlist_id: playlistId, playlist_name: playlistName, product_id: productId, product_name: productName});
      openProductById(productId);
    } else if (type === 'video' && id) {
      const videoId = currentBanner.id;
      const videoTitle = currentBanner.title || null;
      const playlistId = currentBanner.playlistId;
      const playlistName = currentBanner.playlistName || null;
      logActivity('home', 'video', 'click', true, {playlist_id: playlistId, playlist_name: playlistName, video_id: videoId, video_title: videoTitle, target: currentBanner.target, target_type: currentBanner.targetType});
      if (currentBanner.targetType === 'product') openProductById(currentBanner.target);
      else if (currentBanner.targetType === 'category') openCategoryById(currentBanner.target);
    } else if (type === 'image' && id) {
      const imageId = currentBanner.id;
      const imageTitle = currentBanner.title || null;
      const playlistId = currentBanner.playlistId;
      const playlistName = currentBanner.playlistName || null;
      logActivity('home', 'image', 'click', true, {playlist_id: playlistId, playlist_name: playlistName, image_id: imageId, image_title: imageTitle, target: currentBanner.target, target_type: currentBanner.targetType});
      if (currentBanner.targetType === 'product') openProductById(currentBanner.target);
      else if (currentBanner.targetType === 'category') openCategoryById(currentBanner.target);
    }
  };

  const handleScanClick = () => {
    logActivity('home', 'scan-button', 'click', true, null);
    onCartClick();
  };

  const handleSlideIndicatorClick = (index) => {
    const oldBanner = banners[currentSlide];
    const newBanner = banners[index];
    const oldPlaylistId = banners[currentSlide].playlistId;
    const oldPlaylistName = banners[currentSlide].playlistName || null;
    const newPlaylistId = banners[index].playlistId;
    const newPlaylistName = banners[index].playlistName || null;
    logActivity('home', 'slide-indicator', 'click', true, {old_playlist_id: oldPlaylistId, old_playlist_name: oldPlaylistName, old_slide_index: currentSlide, old_slide_id: oldBanner.id, old_slide_title: oldBanner.title || null, old_slide_type: oldBanner.type || null, new_playlist_id: newPlaylistId, new_playlist_name: newPlaylistName, new_slide_index: index, new_slide_id: newBanner.id, new_slide_title: newBanner.title || null, new_slide_type: newBanner.type || null});
    setCurrentSlide(index);
  };

  const onVideoEnd = () => {
    nextSlide(false);
  };

  const onVideoError = () => {
    const video = videoRef.current;
    if (video) {
      const error = video.error;
      const code = error ? error.code : 'unknown';
      console.error('error', `Playback error: ${code} - ${error ? error.message : 'unknown'}`);
    }
    nextSlide(false);
  };

  const handleGoToProducts = () => {
    logActivity('home', 'start-button', 'click', true, null);
    onNavigateToProducts();
  };

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
    }),
  };

  const imgLogo = theme === 'dark' ? (style?.global?.secondaryClientLogo || style?.global?.mainClientLogo || imgDefaultLogo) : (style?.global?.mainClientLogo || style?.global?.secondaryClientLogo || imgDefaultLogo)
  const menuActionStyle = style?.global?.actionBar?.menu;
  const scanActionStyle = style?.global?.actionBar?.scan;
  if (banners.length <= 0) banners.push({ id: null, title: null, price: null, image: imgLogo, is_logo: true });
  const currentBanner = banners[currentSlide];

  return (
    <div className="flex flex-col justify-between bg-back min-h-(--app-h) overflow-hidden">
      <Header
        showThemeChanger={showHeaderThemeChanger}
        showSearchView={showHeaderSearchView}
        cartCount={cartItemsCount}
        showHome={false}
        onHomeClick={undefined}
        onSearchClick={onSearchClick}
        onCartClick={onCartClick}
        onLogoClick={onLogoClick}
        logActivity={logActivity}
        style={style}
      />
      {currentBanner && (
        <div className='a11y-home-banner bg-mid-gradient px-[10vw] py-[100px]' style={{height: 'calc(100% - 112px - 120px)', boxSizing: 'border-box'}}>
          <div className="relative w-full h-full mx-auto">
            <AnimatePresence>
              <motion.div
                key={currentSlide}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={1}
                whileTap={{ scale: 0.8 }}
                whileHover={{ scale: 1.01 }}
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 }
                }}
                onDragStart={() => { wasDragging.current = true; }}
                onDragEnd={(e, { offset }) => {
                  stopSlideInterval();
                  const swipe = offset.x;
                  if (swipe < -100) {
                    nextSlide(true);
                  } else if (swipe > 100) {
                    previousSlide(true);
                  }
                  setTimeout(() => { wasDragging.current = false; }, 0);
                }}
                className={`absolute inset-0 ${currentBanner && currentBanner.type === 'product' ? 'cursor-pointer' : ''}`}
              >
                <div className="relative w-full h-full flex justify-center items-center rounded-[50px] overflow-hidden" onClick={() => { if (wasDragging.current) return; handleItemClick(); }} style={{backgroundColor: currentBanner.type === 'video' ? '#000000' : 'var(--playlist-item-background)'}}>
                  {currentBanner.type === 'product' ? (
                    <>
                      <div className="absolute inset-0 w-full h-full">
                        <ImageWrapper
                          image={currentBanner.image}
                          alt={currentBanner.title}
                          display="block"
                          mode={currentBanner.is_logo ? "contain" : "cover"}
                          width="100%"
                          height="100%"
                          backgroundColor="transparent"
                          margin="0"
                          padding="0"
                          borderRadius="0"
                          transition={true}
                          loadDelay={0.1}
                          onLoad={undefined}
                        />
                      </div>
                      <div className="absolute inset-0"></div>
                      <div className='absolute top-0 right-0 w-full px-10 pt-7 pb-0'>
                        <p className="text-right text-white text-6xl lh-85 main-font-bold" style={{ textShadow: '#000000 4px 4px 4px' }}>{currentBanner.title}</p>
                        <p className="text-right text-white text-5xl lh-85 main-font-regular" style={{ textShadow: '#000000 3px 3px 3px' }}>{currentBanner.price ? new Currency(currency, locale).format(currentBanner.price) : ''}</p>
                      </div>
                    </>
                  ) : null}
                  {currentBanner.type === 'image' ? (
                    <>
                      <div className="absolute inset-0 w-full h-full">
                        <ImageWrapper
                          image={currentBanner.image}
                          alt={currentBanner.title}
                          display="block"
                          mode="cover"
                          width="100%"
                          height="100%"
                          backgroundColor="transparent"
                          margin="0"
                          padding="0"
                          borderRadius="0"
                          transition={true}
                          loadDelay={0.1}
                          onLoad={undefined}
                        />
                      </div>
                    </>
                  ) : null}
                  {currentBanner.type === 'video' ? (
                    <div className="w-auto h-full flex items-center justify-center">
                      <video key={currentBanner.id} ref={videoRef} className="w-full h-[100%] max-w-[1200px]" autoPlay={true} playsInline={true} controls={false} muted={true} loop={currentBanner.loop} onEnded={onVideoEnd} onError={onVideoError} preload='auto'>
                        <source src={currentBanner.video} type="video/mp4"></source>
                      </video>
                    </div>
                  ) : null}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="flex justify-center items-center gap-[12px] mt-[24px]">
            {banners.map((_, index) => (
              <motion.button key={index} onClick={() => handleSlideIndicatorClick(index)} aria-label={`Ir al anuncio ${index + 1}`} className={`a11y-skip-target cursor-pointer h-[10px] rounded-[60px] transition-all ${index === currentSlide ? 'w-[40px] bg-primary' : 'w-[15px] bg-muted'}`} whileTap={{ scale: 0.9 }} />
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-center items-center w-full">
        <div className="px-[33px] pb-[25px] flex gap-3 h-[120px]" style={{maxWidth: '100%', width: 1280}}>
          {scanActionStyle &&
            <motion.button
              onClick={handleScanClick}
              className={`home-button cursor-pointer ${scanActionStyle ? 'double' : 'single'} w-full h-full px-7 py-5 btn-action btn-action-scan rounded-[50px] mx-auto flex items-center justify-center gap-4 relative shadow-[0_4px_12px_rgba(222,45,122,0.3)] hover:shadow-[0_6px_16px_rgba(222,45,122,0.4)] transition-shadow` + (scanActionStyle?.icon ? " flex-col" : "")}
              whileTap={{ scale: 0.9 }}
              whileHover={{ scale: 1.03 }}
            >
              <div className='flex items-center justify-center gap-7'>
                {scanActionStyle?.icon && (
                  <div className='home-button-icon w-[50px] h-[50px]'>
                    <SVGWrapper
                        image={scanActionStyle?.icon}
                        alt={scanActionStyle.altText || "Escanear"}
                        display="block"
                        mode="contain"
                        width="100%"
                        height="100%"
                        backgroundColor="transparent"
                        margin="0"
                        padding="0"
                        borderRadius="0"
                        transition={true}
                        loadDelay={0.1}
                        onLoad={undefined}
                      />
                  </div>
                )}
                <span className="home-button-label">
                  {scanActionStyle?.altText || "Escanear"}
                </span>
                {!scanActionStyle?.icon && <ChevronRight className="" strokeWidth={3} width={50} height={50} />}
              </div>
            </motion.button>
          }
          <motion.button
            onClick={handleGoToProducts}
            className={`home-button cursor-pointer ${scanActionStyle ? 'double' : 'single'} w-full h-full max-w-screen-xl mx-auto px-7 py-5 btn-action btn-action-menu bg-primary rounded-[50px] mx-auto flex items-center justify-center gap-4 relative font-black!` + (menuActionStyle?.icon ? " flex-col" : "")}
            whileTap={{ scale: 0.9 }}
            whileHover={{ scale: 1.03, transition: { duration: 0.2, ease: 'linear'} }}
            animate={{ boxShadow: [
              '0px 0px 30px 0px var(--color-action-menu-glow)',
              '0px 0px 30px 15px var(--color-action-menu-glow)',
              '0px 0px 30px 0px var(--color-action-menu-glow)'
            ] }}
            transition={{
              boxShadow: { delay: 0, duration: 2, repeat: Infinity, ease: 'linear' },
              scale: { duration: 0.2, ease: 'easeOut' }
            }}
            style={{ fontSize: scanActionStyle ? fs(30) : fs(50) }}
          >
            <div className='flex items-center justify-center gap-7'>
              {menuActionStyle?.icon && (
                <div className='home-button-icon w-[50px] h-[50px]'>
                  <SVGWrapper
                    image={menuActionStyle?.icon}
                    alt={menuActionStyle.altText || "Ver Productos"}
                    display="block"
                    mode="contain"
                    width="100%"
                    height="100%"
                    backgroundColor="transparent"
                    margin="0"
                    padding="0"
                    borderRadius="0"
                    transition={true}
                    loadDelay={0.1}
                    onLoad={undefined}
                  />
                </div>
              )}
              <span className="home-button-label">
                {menuActionStyle?.altText || "Ver Productos"}
              </span>
              {!menuActionStyle?.icon && <ChevronRight className="" strokeWidth={3} width={scanActionStyle ? 50 : 80} height={scanActionStyle ? 50 : 80} />}
            </div>
          </motion.button>
        </div>
      </div>
    </div>
  );
}